import * as c from 'utils/comparatorTypes';
import * as t from 'utils/combinatorTypes';
import * as f from 'utils/fieldTypes';
import { objFilterOut } from '@iterable/utils';
import { IS_VALID_HANDLEBAR } from 'utils/validationRegex';
import {
  insertInArray,
  isArray,
  removeFromArray,
  reorderArray,
  replaceInArray,
} from 'utils/helpers';
import { validateDate } from 'utils/validateDate';
import { initialState } from './reducer';
import { isTriggerValueType } from './utils';

/**
 * Structure of search query.
 * {
 *   combinator: '', Overall combinator (Or, And, Not) of Search Query. Applies to all Search Groups.
 *   searchQueries: [ In terms of code, these are the Search Groups.
 *     combinator: '', Combinator of the Search Group. Applies to all Search Queries.
 *     searchQueries: [ In terms of code, these are the Search Queries.
 *       {
 *         dataType: '', Property of the Search Query.
 *         searchCombo: {
 *           combinator: '', Combinator of the Search Query. Applies to all Criteria Properties.
 *           searchQueries: [ In terms of code, these are the Criteria Properties.
 *             {
 *               comparatorType: '',
 *               dataType: '', Copying the Property of the Search Query Here.
 *               field: '',
 *               fieldType: '',
 *               value: '',
 *             },
 *           ],
 *         },
 *       },
 *     ],
 *   ]
 * }
 *
 * Clean Version of structure.
 * {
 *   COMBINATOR,
 *   SEARCH GROUPS: [
 *     {
 *       SEARCH GROUP COMBINATOR,
 *       SEARCH_QUERIES: [
 *         {
 *           PROPERTY,
 *           SEARCH COMBO: {
 *             COMBINATOR,
 *             CRITERIA PROPERTIES: [
 *               {
 *                 COMPARATOR,
 *                 DATA TYPE,
 *                 FIELD,
 *                 FIELD TYPE,
 *                 VALUE,
 *                 VALUE LONG,
 *               }
 *             ]
 *           }
 *         }
 *       ]
 *     }
 *   ]
 * }
 */

export const EMPTY_CRITERIA_PROPERTY = {
  comparatorType: '',
  dataType: '',
  field: '',
  fieldType: '',
};

export const EMPTY_SEARCH_COMBO = {
  combinator: t.AND,
  searchQueries: [EMPTY_CRITERIA_PROPERTY],
};

export const EMPTY_SEARCH_QUERY = {
  dataType: '',
  searchCombo: EMPTY_SEARCH_COMBO,
};

export const EMPTY_SEARCH_GROUP = {
  combinator: t.OR,
  searchQueries: [EMPTY_SEARCH_QUERY],
};

export const EMPTY_COMPARATOR_MESSAGE = 'Missing Comparator';
export const EMPTY_FIELD_MESSAGE = 'Missing Field Value';
export const EMPTY_SEARCH_GROUP_MESSAGE = 'Search Groups must have at least 1 search query';
export const EMPTY_SEARCH_QUERY_MESSAGE = 'Search Queries must have at least 1 field';
export const EMPTY_SEARCH_QUERY_PROPERTY_MESSAGE = 'Search Queries must have a property selected';
export const EMPTY_VALUE_MESSAGE = 'Cannot have empty values';
export const INVALID_DATE_MESSAGE = 'Invalid date';
export const HANDLEBARS_FORBIDDEN_MESSAGE = 'Handlebars not allowed in query.';

export const testConnectedProps = {
  catalogs: {
    collectionBuilder: {
      fieldMappings: [],
    },
  },
  queryBuilder: {
    ...initialState,
    searchQueries: [EMPTY_SEARCH_GROUP],
  },
};

export const testConnectedPropsWithFF = (featureFlags) => ({
  catalogs: {
    collectionBuilder: {
      fieldMappings: [],
    },
  },
  queryBuilder: {
    ...initialState,
    searchQueries: [EMPTY_SEARCH_GROUP],
  },
  userContext: {
    featureFlags,
  },
});

/*------------------------------------------------------------------------------------------------*/
/* Validation Helpers                                                                             */
/*------------------------------------------------------------------------------------------------*/
/* eslint-disable no-param-reassign */

/**
 * validateCriteriaGeoValue
 * Validates a criteria property geo value.
 * @param { Object } criteriaProperty: The criteria property to validate.
 * @returns { Boolean } If there are validation errors.
 */
export const validateCriteriaGeoValue = (criteriaProperty) => {
  const { distance: distanceObj, dynamicValue, location } = criteriaProperty;
  if (dynamicValue !== undefined) {
    return !distanceObj || dynamicValue === '';
  }
  if (distanceObj) {
    distanceObj.distance = parseFloat(distanceObj.distance, 10);
  }
  if (location) {
    location.lat = parseFloat(location.lat, 10);
    location.lon = parseFloat(location.lon, 10);
  }
  return !distanceObj || !location;
};

/**
 * validateCriteriaDateValue
 * Validates a criteria property Date value.
 * @param { Object } criteriaProperty: The criteria property to validate.
 * @returns { Boolean } If there are validation errors.
 */
export const validateCriteriaDateValue = (criteriaProperty) => {
  const { comparatorType, fieldType, value, dynamicValue } = criteriaProperty;
  if (comparatorType === c.IS_SET || comparatorType === c.IS_NOT_SET) {
    return false;
  }
  if (fieldType !== 'date') {
    return false;
  }
  // catches dynamic date values for catalogs, so it doesnt try to validate them
  if (dynamicValue !== undefined) {
    // this returns an error(true) if the dynamic value is not undefined but is still an empty string
    // if the dynamic value is not undefined it should have an actual value
    return dynamicValue === '';
  }

  return validateDate(value);
};

/**
 * validateCriteriaValueIsNotEmpty
 * Validates a criteria property value is not empty.
 * @param { Object } criteriaProperty: The criteria property to validate.
 * @returns { Boolean } If there are validation errors.
 */
export const validateCriteriaValueEmptiness = (criteriaProperty) => {
  const { comparatorType, distance, dynamicValue, fieldType, value, values } = criteriaProperty;
  if ((fieldType === f.DOUBLE || fieldType === f.LONG) && !values) {
    criteriaProperty.value = `${value}`;
  }
  if (distance) {
    return validateCriteriaGeoValue(criteriaProperty);
  } else if (!(comparatorType === c.IS_SET || comparatorType === c.IS_NOT_SET)) {
    if (dynamicValue !== undefined) {
      return dynamicValue === '';
    }
    if (!!values) {
      return values.length === 0;
    }
    return !value;
  } else if (dynamicValue !== undefined) {
    criteriaProperty.dynamicValue = undefined;
  }
  return false;
};

export const valueContainsHandlebars = (value /* : string */) => IS_VALID_HANDLEBAR.test(value);

export const valuesContainHandlebars = (values /* : string[] */) =>
  values.some(valueContainsHandlebars);

/**
 * validateCriteriaValueIsNotHandlebar
 * Validates a criteria property value is not a handlebar
 * @param { Object } criteriaProperty: The criteria property to validate.
 * @returns { Boolean } If there are validation errors.
 */
export const validateCriteriaValueIsNotHandlebar = (criteriaProperty) => {
  const { comparatorType, value, values } = criteriaProperty;
  if (!(comparatorType === c.IS_SET || comparatorType === c.IS_NOT_SET)) {
    if (!!values && valuesContainHandlebars(values)) {
      return true;
    }
    return valueContainsHandlebars(value);
  }
  return false;
};

/**
 * validateCriteriaValue
 * Validates a criteria property value.
 * @param { Object } criteriaProperty: The criteria property to validate.
 * @param { Object } validation: The validation object of validation errors.
 * @param { boolean } disallowHandlebarsInQuery: Whether or not handlebars should be disallowed in query.
 * @returns { Boolean } If there are validation errors.
 */
export const validateCriteriaValue = (
  criteriaProperty,
  validation,
  disallowHandlebarsInQuery = false,
) => {
  if (validateCriteriaValueEmptiness(criteriaProperty)) {
    validation.noValue = EMPTY_VALUE_MESSAGE;
    return true;
  }
  if (disallowHandlebarsInQuery && validateCriteriaValueIsNotHandlebar(criteriaProperty)) {
    validation.cannotContainHandlebars = HANDLEBARS_FORBIDDEN_MESSAGE;
    return true;
  }
  return false;
};

/**
 * validateCriteriaProperty
 * Validates a criteria property.
 * @param { Object } criteriaProperty: The criteria property to validate.
 * @param { Object } validation: The validation object of validation errors.
 * @param { boolean } disallowHandlebarsInQuery: Whether or not handlebars should be disallowed in query.
 * @returns { void }
 */
export const validateCriteriaProperty = (
  criteriaProperty,
  validation,
  disallowHandlebarsInQuery,
) => {
  const { comparatorType, field } = criteriaProperty;
  if (!field) {
    validation.emptyField = EMPTY_FIELD_MESSAGE;
  }
  if (!comparatorType) {
    validation.noCamparator = EMPTY_COMPARATOR_MESSAGE;
  }
  if (
    criteriaProperty &&
    validateCriteriaValue(criteriaProperty, validation, disallowHandlebarsInQuery)
  ) {
    validation.noValue = EMPTY_VALUE_MESSAGE;
  }
  if (criteriaProperty && validateCriteriaDateValue(criteriaProperty)) {
    validation.invalidDate = INVALID_DATE_MESSAGE;
  }
};

/**
 * validateSearchQuery
 * Validates a search query group.
 * @param { Object } searchQuery: The search query group to validate.
 * @param { Object } validation: The validation object of validation errors.
 * @param { boolean } disallowHandlebarsInQuery: Whether or not to allow handlebars in query
 * @returns { void }
 */
export const validateSearchQuery = (searchQuery, validation, disallowHandlebarsInQuery) => {
  const { dataType, searchCombo } = searchQuery;
  if (!dataType) {
    validation.noSeachQueryProperty = EMPTY_SEARCH_QUERY_PROPERTY_MESSAGE;
  } else {
    const { searchQueries } = searchCombo;
    if (isArray(searchQueries)) {
      searchQueries.forEach((criteriaProperty) =>
        validateCriteriaProperty(criteriaProperty, validation, disallowHandlebarsInQuery),
      );
    } else {
      validation.emptyCriteriaProperties = EMPTY_SEARCH_QUERY;
    }
  }
};

/**
 * validateSearchGroup
 * Validates a search group.
 * @param { Object } searchGroup: The search group to validate.
 * @param { Object } validation: The validation object of validation errors.
 * @param { boolean } disallowHandlebarsInQuery: Whether or not to allow handlebars in query
 * @returns { void }
 */
export const validateSearchGroup = (searchGroup, validation, disallowHandlebarsInQuery) => {
  const { searchQueries } = searchGroup;
  if (isArray(searchQueries)) {
    searchQueries.forEach((query) =>
      validateSearchQuery(query, validation, disallowHandlebarsInQuery),
    );
  } else {
    validation.emptySearchGroup = EMPTY_SEARCH_GROUP_MESSAGE;
  }
};

/* eslint-enable no-param-reassign */
/**
 * validateQueryBuilder
 * Validates the search queries of the query builder.
 * @param { Array } searchQueries: The search queries to validate.
 * @param { boolean } disallowHandlebarsInQuery: Whether or not to allow handlebars in query
 * @returns { Array } List of validation errors.
 */
export const validateSearchQueries = (searchQueries, disallowHandlebarsInQuery) => {
  const validation = {};
  searchQueries.forEach((group) =>
    validateSearchGroup(group, validation, disallowHandlebarsInQuery),
  );
  return Object.values(validation);
};

/**
 * formatOrderByForSave
 * Formats order by object for save. If the orderBy object contains a location, it will parse the
 * location into numbers if they are strings.
 * @param { Object } orderBy: The orderBy object to format.
 * @returns { Object } Formatted orderBy object.
 */
export const formatOrderByForSave = (orderBy = {}) => {
  const formattedOrderBy = { ...orderBy };
  if (!orderBy.field) {
    return undefined;
  }
  if (orderBy && orderBy.location && orderBy.location.lat) {
    formattedOrderBy.location.lat = parseFloat(orderBy.location.lat, 10);
    formattedOrderBy.location.lon = parseFloat(orderBy.location.lon, 10);
  }
  return formattedOrderBy;
};

/*------------------------------------------------------------------------------------------------*/
/* Search Query Group Helpers                                                                     */
/*------------------------------------------------------------------------------------------------*/
export const SEARCH_GROUP_ID_PREFIX = 'search-group-';

export const getSearchGroupIndexFromId = (searchGroupId /*: string */) =>
  parseFloat(searchGroupId.replace(SEARCH_GROUP_ID_PREFIX, ''), 10);

/**
 * getSearchGroup
 * Gets the search group of a particular index.
 * @param { Array } searchGroups: The list of search groups.
 * @param { Number } groupIndex: The of the search group to get.
 * @returns { Object } The selected search group.
 */
export const getSearchGroup = (searchGroups, groupIndex) => {
  if (Array.isArray(searchGroups)) {
    return Object.assign({}, searchGroups[groupIndex] || EMPTY_SEARCH_GROUP);
  }
  return EMPTY_SEARCH_GROUP;
};

/**
 * addSearchGroup
 * Add search group to list of search groups.
 * @param { Array } searchGroups: The list of search groups.
 * @param { Object } searchGroup: The of the search group to add.
 * @returns { Array } The list search groups with the new search group.
 */
export const addSearchGroup = (searchGroups, searchGroup) => [...searchGroups, searchGroup];

/**
 * copySearchGroup
 * Copies a search group in list of search groups.
 * @param { Array } searchGroups: The list of search groups.
 * @param { Number } searchGroupIndex: The index of the search group to copy.
 * @returns { Array } The list search groups with the copied search group.
 */
export const copySearchGroup = (searchGroups, searchGroupIndex) =>
  insertInArray(searchGroups, searchGroupIndex, searchGroups[searchGroupIndex]);

/**
 * deleteSearchGroup
 * Deletes a search group in list of search groups.
 * @param { Array } searchGroups: The list of search groups.
 * @param { Number } deleteIndex: The index of the search group to delete.
 * @returns { Array } The list search groups with the deleted search group removed.
 */
export const deleteSearchGroup = (searchGroups, deleteIndex) =>
  removeFromArray(searchGroups, deleteIndex);

/**
 * updateSearchGroup
 * Updates a search group in list of search groups.
 * @param { Array } searchGroups: The list of search groups.
 * @param { Number } searchGroupIndex: The index of the search group to update.
 * @param { Object } searchGroup: The updated search group.
 * @returns { Array } The list search groups with the updated search group.
 */
export const updateSearchGroup = (searchGroups, searchGroupIndex, searchGroup) =>
  replaceInArray(searchGroups, searchGroupIndex, searchGroup);

/*------------------------------------------------------------------------------------------------*/
/* Search Query Helpers                                                                           */
/*------------------------------------------------------------------------------------------------*/
/**
 * getSearchQueries
 * Get the list of search queries from a search group.
 * @param { Object } searchGroup: The search group to get the search queries from.
 * @returns { Array } The search queries.
 */
export const getSearchQueries = (searchGroup) => {
  const { searchQueries } = searchGroup;
  return searchQueries || [];
};

/**
 * getSearchQuery
 * Get the search query of a particular index from a search group.
 * @param { Object } searchGroup: The search group to get the search query from.
 * @param { Number } queryIndex: The index of the search query to get.
 * @returns { Object } The search query.
 */
export const getSearchQuery = (searchGroup, queryIndex) => {
  const searchQueries = getSearchQueries(searchGroup);
  return searchQueries[queryIndex];
};

/**
 * addSearchQuery
 * Adds a search query to a search group.
 * @param { Array } searchGroups: The list of search groups to add search query to.
 * @param { Number } searchGroupIndex: The index of the search group to add search query to.
 * @param { Object } searchQuery: The search query to add.
 * @returns { Array } The updated search groups with the newly added search query.
 */
export const addSearchQuery = (searchGroups, searchGroupIndex, searchQuery) => {
  const searchGroup = getSearchGroup(searchGroups, searchGroupIndex);
  const searchQueries = getSearchQueries(searchGroup);
  const updatedSearchQueries = [...searchQueries, searchQuery];
  const updatedSearchGroup = { ...searchGroup, searchQueries: updatedSearchQueries };
  return updateSearchGroup(searchGroups, searchGroupIndex, updatedSearchGroup);
};

/**
 * copySearchQuery
 * Copies a search query from a search group.
 * @param { Array } searchGroups: The list of search groups to copy search query.
 * @param { Number } searchGroupIndex: The index of the search group to copy search query from.
 * @param { Object } searchQueryIndex: The index of the search query to copy.
 * @returns { Array } The updated search groups with the copied search query.
 */
export const copySearchQuery = (searchGroups, searchGroupIndex, searchQueryIndex) => {
  const searchGroup = getSearchGroup(searchGroups, searchGroupIndex);
  const searchQueries = getSearchQueries(searchGroup);
  const updatedSearchQueries = insertInArray(
    searchQueries,
    searchQueryIndex,
    searchQueries[searchQueryIndex],
  );
  const updatedSearchGroup = { ...searchGroup, searchQueries: updatedSearchQueries };
  return updateSearchGroup(searchGroups, searchGroupIndex, updatedSearchGroup);
};

/**
 * deleteSearchQuery
 * Deletes a search query from a search group.
 * @param { Array } searchGroups: The list of search groups to delete search query.
 * @param { Number } searchGroupIndex: The index of the search group to delete search query from.
 * @param { Number } deleteIndex: The index of the search query to delete.
 * @returns { Array } The updated search groups with the deleted search query removed.
 */
export const deleteSearchQuery = (searchGroups, searchGroupIndex, deleteIndex) => {
  const searchGroup = getSearchGroup(searchGroups, searchGroupIndex);
  const searchQueries = getSearchQueries(searchGroup);
  const updatedSearchQueries = removeFromArray(searchQueries, deleteIndex);
  const updatedSearchGroup = { ...searchGroup, searchQueries: updatedSearchQueries };
  return updateSearchGroup(searchGroups, searchGroupIndex, updatedSearchGroup);
};

/**
 * moveSearchQuery
 * Moves a search query from a search group to another.
 * @param { Array } searchGroups: The list of search groups to move search query.
 * @param { Number } searchGroupIndex: The index of the search group to move search query from.
 * @param { Number } searchQueryIndex: The index of the search query to move.
 * @param { Number } newSearchGroupIndex: The new index of the group to move search query to.
 * @param { Number } newSearchQueryIndex: The new index in the search group to move search query to.
 * @returns { Array } The updated search groups with the moved search query.
 */
export const moveSearchQuery = (
  searchGroups,
  searchGroupIndex,
  searchQueryIndex,
  newSearchGroupIndex,
  newSearchQueryIndex,
) => {
  const searchGroup = getSearchGroup(searchGroups, searchGroupIndex);
  const searchQuery = getSearchQuery(searchGroup, searchQueryIndex);

  // Remove search query from group.
  const updatedSearchGroups = deleteSearchQuery(searchGroups, searchGroupIndex, searchQueryIndex);

  // Insert search query to group.
  const destGroup = getSearchGroup(updatedSearchGroups, newSearchGroupIndex);
  const destQueries = getSearchQueries(destGroup);
  const updatedDestQueries = insertInArray(destQueries, newSearchQueryIndex, searchQuery);
  const updatedDestGroup = { ...destGroup, searchQueries: updatedDestQueries };
  return updateSearchGroup(updatedSearchGroups, newSearchGroupIndex, updatedDestGroup);
};

/**
 * reorderSearchQuery
 * Reorders a search query in a search group.
 * @param { Array } searchGroups: The list of search groups to reorder search query.
 * @param { Number } searchGroupIndex: The index of the search group to reorder search query from.
 * @param { Number } searchQueryIndex: The index of the search query to reorder.
 * @param { Number } newSearchQueryIndex: The new index to move the search query to.
 * @returns { Array } The updated search groups with the reordered search queries.
 */
export const reorderSearchQuery = (
  searchGroups,
  searchGroupIndex,
  searchQueryIndex,
  newSearchQueryIndex,
) => {
  const searchGroup = getSearchGroup(searchGroups, searchGroupIndex);
  const searchQueries = getSearchQueries(searchGroup);
  const updatedSearchQueries = reorderArray(searchQueries, searchQueryIndex, newSearchQueryIndex);
  const updatedSearchGroup = { ...searchGroup, searchQueries: updatedSearchQueries };
  return updateSearchGroup(searchGroups, searchGroupIndex, updatedSearchGroup);
};

/**
 * updateSearchQuery
 * Updates a search query of a particular index from a search group.
 * @param { Array } searchGroups: The list of search groups.
 * @param { Number } searchGroupIndex: The index of the search group the search query is in.
 * @param { Number } searchQueryIndex: The index of the search query.
 * @param { Object } searchQuery: The updated search query.
 * @returns { Array } The search queries.
 */
export const updateSearchQuery = (
  searchGroups,
  searchGroupIndex,
  searchQueryIndex,
  searchQuery,
) => {
  const searchGroup = getSearchGroup(searchGroups, searchGroupIndex);
  const searchQueries = getSearchQueries(searchGroup);
  const searchQueryToUpdate = getSearchQuery(searchGroup, searchQueryIndex);
  const updatedQuery = { ...searchQueryToUpdate, ...searchQuery };

  // each search query has a search combo field that has nested search queries inside of it
  // (look at the structure near the top of this file for a visual reference)
  // when updating the "dataType" of a search query, we also need to update the "dataType" of the nested
  // search queries to match the new data type. Otherwise the backend will error out when trying to save.
  const searchQueryComboToUpdate = { ...searchQueryToUpdate.searchCombo };
  searchQueryComboToUpdate.searchQueries = searchQueryComboToUpdate.searchQueries.map((query) => {
    return searchQuery.dataType ? { ...query, dataType: searchQuery.dataType } : query;
  });

  updatedQuery.searchCombo = searchQuery.searchCombo || searchQueryComboToUpdate;

  const updatedSearchQueries = replaceInArray(searchQueries, searchQueryIndex, updatedQuery);
  const updatedSearchGroup = { ...searchGroup, searchQueries: updatedSearchQueries };
  return updateSearchGroup(searchGroups, searchGroupIndex, updatedSearchGroup);
};

/*------------------------------------------------------------------------------------------------*/
/* Search Criteria Helpers                                                                        */
/*------------------------------------------------------------------------------------------------*/
/**
 * getSearchCombo
 * Get the search combo from a search query .
 * @param { Object } searchQuery: The search query to get the search combo from.
 * @returns { Object } The search combo.
 */
export const getSearchCombo = (searchQuery) => searchQuery.searchCombo || EMPTY_SEARCH_COMBO;

/**
 * getCriteriaProperties
 * Get the criteriaProperties from a search query.
 * @param { Object } searchQuery: The search query to get the criteria properties from.
 * @returns { Array } The criteria properties.
 */
export const getCriteriaProperties = (searchQuery) => {
  const searchCombo = getSearchCombo(searchQuery);
  const { searchQueries } = searchCombo;
  if (Array.isArray(searchQueries)) {
    return searchQueries;
  }
  return [];
};

/**
 * getCriteriaProperty
 * Get the criteria property of a certain index from a search query.
 * @param { Object } searchQuery: The search query to get the criteria property from.
 * @param { Number } criteriaPropertyIndex: The index of the criteia property to get.
 * @returns { Object } The criteria property.
 */
export const getCriteriaProperty = (searchQuery, criteriaPropertyIndex) => {
  const criteriaProperties = getCriteriaProperties(searchQuery);
  return criteriaProperties[criteriaPropertyIndex];
};

/**
 * getQueries
 * Helper function for criteria property function helpers to get search group objects.
 * @param { Array } groups: The list of search groups.
 * @param { Number } groupIndex: The index of the search group to get.
 * @param { Number } queryIndex: The index of the search query to get.
 * @returns { Object } Object containing the search group, search queries, search query, search combo, and criteria properties.
 */
export const getQueries = (groups, groupIndex, queryIndex) => {
  const group = getSearchGroup(groups, groupIndex);
  const queries = getSearchQueries(group);
  const query = getSearchQuery(group, queryIndex);
  const combo = getSearchCombo(query);
  const criteriaProperties = getCriteriaProperties(query);
  return { groups, group, queries, query, combo, criteriaProperties };
};

/**
 * updateQueries
 * Helper function to update criteria properties and chain up to update parent objects.
 * @param { Array } criteriaProperties: The list of updated criteria properties.
 * @param { Object } searchQueries: The values given back from the getQueries() function.
 * @param { Number } searchGroupIndex: The index of the search group to update.
 * @param { Number } searchQueryIndex: The index of the search query to update.
 * @returns { Array } The updated search groups.
 */
export const updateQueries = (
  criteriaProperties,
  searchQueries,
  searchGroupIndex,
  searchQueryIndex,
) => {
  const { groups, group, queries, query, combo } = searchQueries;
  const updatedSearchCombo = { ...combo, searchQueries: criteriaProperties };
  const updatedSearchQuery = { ...query, searchCombo: updatedSearchCombo };
  const updatedSearchQueries = replaceInArray(queries, searchQueryIndex, updatedSearchQuery);
  const updatedSearchGroup = { ...group, searchQueries: updatedSearchQueries };
  return updateSearchGroup(groups, searchGroupIndex, updatedSearchGroup);
};

/**
 * addCriteriaProperty
 * Adds a criteria property to the list of criteria properties in a given search query.
 * @param { Array } groups: The list of updated criteria properties.
 * @param { Number } groupIndex: The index of the search group to add criteria property.
 * @param { Number } queryIndex: The index of the search query to add criteria property.
 * @param { Object } criteriaProperty: The criteria property to add.
 * @returns { Array } The updated search groups with the newly added criteria property.
 */
export const addCriteriaProperty = (groups, groupIndex, queryIndex, criteriaProperty) => {
  const searchQueries = getQueries(groups, groupIndex, queryIndex);
  const { criteriaProperties } = searchQueries;
  const properties = [...criteriaProperties, criteriaProperty];
  return updateQueries(properties, searchQueries, groupIndex, queryIndex);
};

/**
 * copyCriteriaProperty
 * Copies a criteria property from a list of criteria properties in a given search query.
 * @param { Array } groups: The list of updated criteria properties.
 * @param { Number } groupIndex: The index of the search group to copy criteria property.
 * @param { Number } queryIndex: The index of the search query to copy criteria property.
 * @param { Number } criteriaPropertyIndex: The indoex of the criteria property to copy.
 * @returns { Array } The updated search groups with the copied criteria property.
 */
export const copyCriteriaProperty = (groups, groupIndex, queryIndex, criteriaPropertyIndex) => {
  const searchQueries = getQueries(groups, groupIndex, queryIndex);
  const { criteriaProperties } = searchQueries;
  const properties = insertInArray(
    criteriaProperties,
    criteriaPropertyIndex,
    criteriaProperties[criteriaPropertyIndex],
  );
  return updateQueries(properties, searchQueries, groupIndex, queryIndex);
};

/**
 * deleteCriteriaProperty
 * Deletes a criteria property from a list of criteria properties in a given search query.
 * @param { Array } groups: The list of updated criteria properties.
 * @param { Number } groupIndex: The index of the search group to delete criteria property.
 * @param { Number } queryIndex: The index of the search query to delete criteria property..
 * @param { Number } criteriaPropertyIndex: The indoex of the criteria property to delete.
 * @returns { Array } The updated search groups with the deleted criteria property removed.
 */
export const deleteCriteriaProperty = (groups, groupIndex, queryIndex, criteriaPropertyIndex) => {
  const searchQueries = getQueries(groups, groupIndex, queryIndex);
  const { criteriaProperties } = searchQueries;
  const properties = removeFromArray(criteriaProperties, criteriaPropertyIndex);
  return updateQueries(properties, searchQueries, groupIndex, queryIndex);
};

/**
 * updateCriteriaProperty
 * Updates a criteria property from a list of criteria properties in a given search query.
 * @param { Array } groups: The list of updated criteria properties.
 * @param { Number } groupIndex: The index of the search group to update criteria property.
 * @param { Number } queryIndex: The index of the search query to update criteria property.
 * @param { Number } criteriaPropertyIndex: The index of the criteria property to update.
 * @param { Object } criteriaProperty: The updated criteria property.
 * @returns { Array } The updated search groups with the updated criteria property.
 */
export const updateCriteriaProperty = (
  groups,
  groupIndex,
  queryIndex,
  criteriaPropertyIndex,
  criteriaProperty,
) => {
  const searchQueries = getQueries(groups, groupIndex, queryIndex);
  const { query, criteriaProperties } = searchQueries;

  let propertyToUpdate = getCriteriaProperty(query, criteriaPropertyIndex);
  // if current property field has a multiselect value,
  // remove `value` key from search queries property data to
  // prevent issues when saving, as multiselect fields rely on `values` key instead

  if (
    criteriaProperty?.values &&
    propertyToUpdate?.values === undefined &&
    propertyToUpdate?.value !== undefined
  ) {
    propertyToUpdate = objFilterOut(propertyToUpdate, ['value']);
  } else if (Array.isArray(propertyToUpdate?.values) && propertyToUpdate?.value === undefined) {
    // if current property field has values and criteria property doesn't add values,
    // remove `values` key from search queries property data to
    // prevent issues when saving, as non multiselect fields rely on `value` key instead
    propertyToUpdate = objFilterOut(propertyToUpdate, ['values']);
  }

  const updatedProperty = { ...propertyToUpdate, ...criteriaProperty };
  const properties = replaceInArray(criteriaProperties, criteriaPropertyIndex, updatedProperty);
  return updateQueries(properties, searchQueries, groupIndex, queryIndex);
};

/*------------------------------------------------------------------------------------------------*/
/* Comparator Helper                                                                              */
/*------------------------------------------------------------------------------------------------*/
/**
 * Parses the dynamic value
 * @param { String } valueType The type of value
 * @returns string | undefined
 */
export const getDynamicValue = (valueType) => (isTriggerValueType(valueType) ? '' : undefined);

/**
 * getUpdatedCriteriaPropertyForComparator
 * Gets the updated criteria property values when changing comparators.
 * @param { String } comparatorType: The comparator type.
 * @param { String } valueType: If the field id dynamic.
 * @returns { Object } The updated criteria property values.
 */
export const getUpdatedCriteriaPropertyForComparator = (comparatorType, valueType) => {
  switch (comparatorType) {
    case c.IS_SET:
    case c.IS_NOT_SET:
      return {
        comparatorType,
        value: '',
        dynamicValue: getDynamicValue(valueType),
      };
    case c.IS_ONE_OF:
      return { comparatorType: c.EQUALS, values: [] };
    case c.IS_NOT_ONE_OF:
      return { comparatorType: c.DOES_NOT_EQUAL, values: [] };
    default:
      return { comparatorType };
  }
};

/*------------------------------------------------------------------------------------------------*/
/* Dynamic Field Counter Helper                                                                   */
/*------------------------------------------------------------------------------------------------*/
/**
 * checkDynamicFieldsInCriteriaProperties
 * Checks if dynamic fields exists in a list of criteria proerties.
 * @param { Array } criteriaProperties: The criteria properties to check dynamic fields.
 * @returns { Boolean } If a dynamic field exists in a list of criteria properties.
 */
export const checkDynamicFieldsInCriteriaProperties = (criteriaProperties) => {
  if (isArray(criteriaProperties)) {
    const property = criteriaProperties.find(({ dynamicValue }) => dynamicValue !== undefined);
    return property !== undefined;
  }
  return false;
};

/**
 * checkDynamicFieldsInSearchQuery
 * Checks for dynamic fields in a search query.
 * @param { Object } searchQuery: The search query to check dynamic fields.
 * @returns { Boolean } If a dynamic field exists in a search query.
 */
export const checkDynamicFieldsInSearchQuery = (searchQuery = {}) => {
  const criteriaProperties = getCriteriaProperties(searchQuery);
  return checkDynamicFieldsInCriteriaProperties(criteriaProperties);
};

/**
 * checkDynamicFieldsInSearchGroup
 * Checks for dynamic fields in a search group.
 * @param { Object } searchGroup: The search group to check dynamic fields.
 * @returns { Boolean } If a dynamic field exists in a search group.
 */
export const checkDynamicFieldsInSearchGroup = (searchGroup = {}) => {
  const searchQueries = getSearchQueries(searchGroup);

  if (isArray(searchQueries)) {
    return searchQueries.find((searchQuery) => checkDynamicFieldsInSearchQuery(searchQuery));
  }
  return false;
};

/**
 * checkDynamicFieldsInSearchGroups
 * Checks for dynamic fields in a list of search groups.
 * @param { Array } searchGroups: The list of search groups to check dynamic fields.
 * @returns { Boolean } If a dynamic field exists in the search groups.
 */
export const checkDynamicFieldsInSearchGroups = (searchGroups = []) => {
  if (isArray(searchGroups)) {
    return searchGroups.find((searchGroup) => checkDynamicFieldsInSearchGroup(searchGroup));
  }
  return false;
};

/**
 * parseSearchQueryResponseData
 * Parses the response data of a search query to pull out search query data and dynamicFieldCount.
 * @param { Object } responseData: The response data to parse.
 * @returns { Object } Formatted response data.
 */
export const parseSearchQueryResponseData = (responseData = {}) => {
  const { searchQuery, ...otherProps } = responseData;
  let hasDynamicFields = false;

  if (responseData && responseData.searchQuery) {
    if (isArray(responseData.searchQuery.searchQueries)) {
      hasDynamicFields = checkDynamicFieldsInSearchGroups(
        responseData.searchQuery.searchQueries,
        true,
      );
    }
  }
  return { hasDynamicFields, ...searchQuery, ...otherProps };
};

/**
 * checkForDynamicFields
 * Determines when to check for dynamic fields.
 * @param { Array } searchQueries: The search queries to check for dynamic fields.
 * @param { Boolean } dynamicToggleShown: If the dynamic field toggle is displayed.
 * @param { Boolean } hasDynamicFields: If the query already has dynamic fields.
 * @param { Boolean } doCheckIfDynamic: Does a check if dynamic fields already exist.
 * @param { Boolean } forceCheck: Does a check regaurdless if query has dynamic fields or not.
 * @returns { Boolean } If searchQUeries contain a dynamic field.
 */
export const checkForDynamicFields = (
  searchQueries,
  dynamicToggleShown,
  hasDynamicFields,
  doCheckIfDynamic,
  forceCheck,
) => {
  const shouldCheck = (doCheckIfDynamic && hasDynamicFields) || forceCheck;
  if (shouldCheck && dynamicToggleShown) {
    return checkDynamicFieldsInSearchGroups(searchQueries);
  }
  return false;
};

/**
 *
 * Gets catalog name from collection state and query state.
 * If collection state is undefined it will default to query state
 */
export const getCatalogName = ({ queryBuilder, catalogs }) => {
  const catalogNameFromQueryState = queryBuilder?.catalogName;
  const catalogNameFromCollection = catalogs?.collectionBuilder?.catalog;
  return catalogNameFromCollection || catalogNameFromQueryState;
};

/**
 *
 * Gets the default combinator for queryBuilder. If feature flag is on default is "All"
 * otherwise return a specified default or "Or";
 */
export const getDefaultCombinator = ({ userContext }, defaultCombinator = t.OR) => {
  const isAllCombinatorSearchQueryOn = userContext?.featureFlags?.DefaultCombinatorsToAll;
  return isAllCombinatorSearchQueryOn ? t.AND : defaultCombinator;
};
